/**
 * Pure transforms from raw sleep sessions to the rows the Sleep view charts —
 * a nightly stacked-duration bar and a bedtime-consistency scatter. Kept free of
 * React/ECharts so the bucketing and stage math are unit-tested directly.
 *
 * Stage durations arrive in MINUTES from both ingest paths: Apple Health writes
 * `{ core, deep, rem, awake }` (minutes) and Fitbit writes
 * `{ summary: { deep|light|rem|wake: { minutes } } }`. We fold `light`→core and
 * `wake`→awake, and emit everything in HOURS for the chart.
 */

import type { SleepSession } from '../api.js';

export interface StageHours {
  core: number;
  deep: number;
  rem: number;
  awake: number;
}

export interface SleepNight {
  /** Local calendar date of the bedtime (YYYY-MM-DD) — the night's label. */
  date: string;
  /** Total time asleep+awake for the session, in hours. */
  durationHours: number;
  /** Per-stage hours when a breakdown is present, else null (plain duration bar). */
  stages: StageHours | null;
  /**
   * Bedtime as a "night hour" for the scatter: local hour-of-day, with
   * after-midnight times shifted past 24 (00:30 → 24.5) so 11pm and 1am sit next
   * to each other instead of at opposite ends. null when the start is unusable.
   */
  bedtimeHour: number | null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Extract per-stage MINUTES from a session's `stages` blob (Apple or Fitbit shape). */
function stageMinutes(stages: unknown): StageHours | null {
  if (!isObj(stages)) return null;

  // Fitbit: stages.summary.{deep,light,rem,wake}.minutes
  if (isObj(stages.summary)) {
    const s = stages.summary as Record<string, unknown>;
    const min = (k: string): number | null => (isObj(s[k]) ? num((s[k] as Record<string, unknown>).minutes) : null);
    const deep = min('deep');
    const light = min('light');
    const rem = min('rem');
    const wake = min('wake');
    if (deep !== null || light !== null || rem !== null || wake !== null) {
      return { core: light ?? 0, deep: deep ?? 0, rem: rem ?? 0, awake: wake ?? 0 };
    }
  }

  // Apple Health: flat core/deep/rem/awake minutes.
  const core = num(stages.core);
  const deep = num(stages.deep);
  const rem = num(stages.rem);
  const awake = num(stages.awake);
  if (core !== null || deep !== null || rem !== null || awake !== null) {
    return { core: core ?? 0, deep: deep ?? 0, rem: rem ?? 0, awake: awake ?? 0 };
  }
  return null;
}

/** Local wall-clock parts of an ISO instant, given `local = UTC + tzOffsetMin`. */
function localParts(iso: string, tzOffsetMin: number): { date: string; hour: number } | null {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const local = new Date(ms + tzOffsetMin * 60_000);
  const y = local.getUTCFullYear();
  const mo = String(local.getUTCMonth() + 1).padStart(2, '0');
  const d = String(local.getUTCDate()).padStart(2, '0');
  return { date: `${y}-${mo}-${d}`, hour: local.getUTCHours() + local.getUTCMinutes() / 60 };
}

/**
 * Transform sessions (newest-first or any order) into nightly rows sorted oldest
 * first. `tzOffsetMinutes` places bedtimes in the user's local frame (ET default).
 */
export function toSleepNights(sessions: SleepSession[], tzOffsetMinutes = -240): SleepNight[] {
  const nights: SleepNight[] = [];
  for (const s of sessions) {
    const start = localParts(s.startedAt, tzOffsetMinutes);
    if (!start) continue;
    const startMs = new Date(s.startedAt).getTime();
    const endMs = new Date(s.endedAt).getTime();
    const durationHours =
      typeof s.durationSec === 'number' && s.durationSec > 0
        ? s.durationSec / 3600
        : Number.isFinite(endMs) && Number.isFinite(startMs)
          ? Math.max(0, (endMs - startMs) / 3_600_000)
          : 0;

    const mins = stageMinutes(s.stages);
    const stages: StageHours | null = mins
      ? { core: mins.core / 60, deep: mins.deep / 60, rem: mins.rem / 60, awake: mins.awake / 60 }
      : null;

    // Shift after-midnight bedtimes past 24 for a continuous consistency strip.
    const bedtimeHour = start.hour < 12 ? start.hour + 24 : start.hour;

    nights.push({ date: start.date, durationHours, stages, bedtimeHour });
  }
  return nights.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Mean nightly sleep duration in hours (0 when there are no nights). */
export function avgDurationHours(nights: SleepNight[]): number {
  if (nights.length === 0) return 0;
  const total = nights.reduce((sum, n) => sum + n.durationHours, 0);
  return total / nights.length;
}
